<template>
  <div>
    <v-dialog v-model="dialog" max-width="900px" persistent scrollable>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-cart-plus</v-icon>
          Add Purchase Order
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-form ref="form" v-model="valid">
            <!-- Customer PO Sub-type -->
            <v-card outlined class="mb-4 pa-3">
              <v-card-subtitle class="pa-0 mb-2 font-weight-bold">
                Customer Type *
              </v-card-subtitle>
              <v-row>
                <v-col cols="12">
                  <v-radio-group
                    v-model="form.customer_po_subtype"
                    row
                    dense
                    hide-details
                    class="mt-0"
                    :rules="[rules.required]"
                  >
                    <v-radio label="Walk-in" value="walk_in"></v-radio>
                    <v-radio label="Private" value="private"></v-radio>
                    <v-radio label="Government" value="government"></v-radio>
                  </v-radio-group>
                </v-col>
              </v-row>
            </v-card>

            <v-row>
              <!-- Customer Selection -->
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="form.customer_id"
                  :items="customers"
                  item-text="customer_name"
                  item-value="id"
                  label="Customer *"
                  prepend-icon="mdi-account"
                  :rules="[rules.required]"
                  clearable
                  outlined
                  dense
                ></v-autocomplete>
              </v-col>

              <!-- Name (auto-filled from customer, can be overridden) -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.name"
                  label="Name"
                  prepend-icon="mdi-account-outline"
                  outlined
                  dense
                  hint="Person/entity requesting (auto-filled from customer if selected)"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- Department -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.department"
                  label="Department"
                  prepend-icon="mdi-domain"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <!-- PO Number -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.po_number"
                  label="PO Number *"
                  prepend-icon="mdi-file-document"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <!-- PO Date (Filed) -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.po_date"
                  label="PO Date (Filed) *"
                  type="date"
                  prepend-icon="mdi-calendar"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>

              <!-- Delivery Term -->
              <v-col cols="12" md="6">
                <v-select
                  v-model.number="form.delivery_term_days"
                  :items="deliveryTermOptions"
                  label="Delivery Term *"
                  prepend-icon="mdi-truck-fast"
                  :rules="[rules.required]"
                  outlined
                  dense
                  hint="Working days allowed to deliver"
                  persistent-hint
                ></v-select>
              </v-col>

              <!-- Completed Date -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.completed_date"
                  label="Completed Date"
                  type="date"
                  prepend-icon="mdi-calendar-check"
                  outlined
                  dense
                  clearable
                  hint="Leave blank until delivered / completed"
                  persistent-hint
                ></v-text-field>
              </v-col>

              <!-- Amount -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.amount"
                  label="Amount"
                  type="number"
                  prepend-icon="mdi-currency-php"
                  :rules="[rules.positive]"
                  outlined
                  dense
                  min="0"
                  step="0.01"
                ></v-text-field>
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.status"
                  :items="statusOptions"
                  label="Status *"
                  prepend-icon="mdi-flag"
                  :rules="[rules.required]"
                  outlined
                  dense
                ></v-select>
              </v-col>
            </v-row>

            <!-- File Attachments (Multiple) -->
            <v-file-input
              v-model="attachmentFiles"
              label="Attachments (JPEG, PNG, PDF)"
              prepend-icon="mdi-paperclip"
              accept="image/*,.pdf"
              outlined
              dense
              clearable
              show-size
              multiple
              :hint="
                attachmentFiles && attachmentFiles.length > 0
                  ? attachmentFiles.length + ' file(s) selected'
                  : ''
              "
              persistent-hint
            ></v-file-input>
            <div
              v-if="attachmentFiles && attachmentFiles.length > 0"
              class="mb-4"
            >
              <v-chip
                v-for="(file, idx) in attachmentFiles"
                :key="idx"
                close
                small
                class="mr-2 mb-1"
                @click:close="removeAttachment(idx)"
              >
                <v-icon left small>mdi-file</v-icon>
                {{ file.name }}
              </v-chip>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4 d-block">
          <v-progress-linear
            v-if="submitting && uploadProgress > 0"
            :value="uploadProgress"
            color="primary"
            height="20"
            class="mb-3 rounded"
            striped
          >
            <template v-slot:default>
              <strong class="white--text caption">{{ uploadProgress }}%</strong>
            </template>
          </v-progress-linear>
          <div class="d-flex">
            <v-btn
              color="primary"
              :disabled="!valid"
              :loading="submitting"
              @click="submitForm"
            >
              <v-icon left>mdi-content-save</v-icon>
              Save Purchase Order
            </v-btn>
            <v-btn text @click="closeModal">
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  name: "AddCustomerPurchaseOrderModal",

  data() {
    const today = new Date().toISOString().substring(0, 10);

    return {
      dialog: false,
      valid: false,
      submitting: false,
      attachmentFiles: [],
      uploadProgress: 0,

      form: {
        customer_id: null,
        po_number: "",
        po_date: today,
        completed_date: null,
        delivery_term_days: 15,
        amount: 0,
        status: "Pending",
        po_types: ["customer_po"],
        customer_po_subtype: "walk_in",
        name: "",
        department: "",
      },

      deliveryTermOptions: [
        { text: "15 working days", value: 15 },
        { text: "30 working days", value: 30 },
      ],

      statusOptions: [
        "Pending",
        "Approved",
        "Processing",
        "Completed",
        "Cancelled",
        "On Hold",
      ],

      rules: {
        required: (value) => !!value || "This field is required",
        positive: (value) => value >= 0 || "Amount must be positive",
      },
    };
  },

  computed: {
    ...mapGetters({
      customers: "customer/customers",
      user: "auth/user",
    }),
  },

  watch: {
    "form.customer_id"(val) {
      // Auto-fill name from selected customer
      if (val) {
        const customer = this.customers.find((c) => c.id === val);
        if (customer) {
          this.form.name = customer.customer_name;
        }
      }
    },
  },

  methods: {
    ...mapActions({
      get_customers: "customer/get_customers",
      add_customer_purchase_order:
        "customer_purchase_order/add_customer_purchase_order",
    }),

    open() {
      this.dialog = true;
      if (this.customers.length === 0) {
        this.get_customers();
      }
    },

    closeModal() {
      this.dialog = false;
      this.resetForm();
    },

    removeAttachment(index) {
      this.attachmentFiles.splice(index, 1);
    },

    async submitForm() {
      if (!this.$refs.form.validate()) {
        return;
      }

      this.submitting = true;
      this.uploadProgress = 0;

      try {
        const formData = new FormData();

        if (this.form.customer_id) {
          formData.append("customer_id", this.form.customer_id);
        }
        formData.append("po_number", this.form.po_number);
        formData.append("po_date", this.form.po_date);
        formData.append("completed_date", this.form.completed_date || "");
        formData.append("delivery_term_days", this.form.delivery_term_days || "");
        formData.append("amount", this.form.amount || 0);
        formData.append("status", this.form.status);
        formData.append("po_types", JSON.stringify(this.form.po_types));
        formData.append(
          "customer_po_subtype",
          this.form.customer_po_subtype || ""
        );
        formData.append("name", this.form.name || "");
        formData.append("department", this.form.department || "");
        formData.append("prepared_by_id", this.user.id);

        if (this.attachmentFiles && this.attachmentFiles.length > 0) {
          this.attachmentFiles.forEach((file) => {
            formData.append("attachments[]", file);
          });
        }

        await this.add_customer_purchase_order({
          _formData: formData,
          onUploadProgress: (e) => {
            if (e.total) {
              this.uploadProgress = Math.round((e.loaded * 100) / e.total);
            }
          },
        });
        this.$emit("success", "Purchase order created successfully!");
        this.closeModal();
      } catch (error) {
        console.error("Error creating purchase order:", error);
        this.$emit(
          "error",
          error.response?.data?.message || "Failed to create purchase order"
        );
      } finally {
        this.submitting = false;
        this.uploadProgress = 0;
      }
    },

    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }
      const today = new Date().toISOString().substring(0, 10);

      this.form = {
        customer_id: null,
        po_number: "",
        po_date: today,
        completed_date: null,
        delivery_term_days: 15,
        amount: 0,
        status: "Pending",
        po_types: ["customer_po"],
        customer_po_subtype: "walk_in",
        name: "",
        department: "",
      };

      this.attachmentFiles = [];
      this.uploadProgress = 0;
    },
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 8px;
}
</style>
